/**
 * Zustand store for the knowledge browser dialog (UI-only state).
 *
 * Server data — search results, entry bodies — lives in TanStack Query.
 * This store holds the bits the dialog needs to coordinate across renders:
 *
 *  - `isOpen` so any component (chip, future menu entry) can open it.
 *  - `selectedEntryId` so the citation chip can deep-link to a specific
 *    entry, and so the result list and detail pane stay in sync without
 *    prop drilling through the dialog tree.
 *  - `query` + `kinds` so opening the dialog from a chip can preserve the
 *    last-used filter, and so a controlled search box maps cleanly onto
 *    the TanStack Query key.
 *
 * Kept feature-local per the "compose, don't fragment" rule — promote to
 * `app-state-*` only when a second feature needs to drive this dialog.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type { KnowledgeKind } from '../api/knowledgeTypes';

export interface KnowledgeBrowserState {
  isOpen: boolean;
  /**
   * The result currently driving the right-side detail pane. Driven by
   * both keyboard nav (↑/↓) and clicks — the spotlight design treats both
   * as the same "navigate to this option" gesture. A non-null value means
   * the detail pane is open.
   */
  selectedEntryId: string | null;
  query: string;
  kinds: KnowledgeKind[];
}

export interface KnowledgeBrowserActions {
  open: (entryId?: string | null) => void;
  close: () => void;
  selectEntry: (entryId: string | null) => void;
  setQuery: (query: string) => void;
  toggleKind: (kind: KnowledgeKind) => void;
  setKinds: (kinds: KnowledgeKind[]) => void;
  reset: () => void;
}

const initialState: KnowledgeBrowserState = {
  isOpen: false,
  selectedEntryId: null,
  query: '',
  kinds: [],
};

export const useKnowledgeBrowserStore = create<KnowledgeBrowserState & KnowledgeBrowserActions>()(
  devtools(
    (set) => ({
      ...initialState,
      // A caller can pass `entryId` to deep-link straight into the detail
      // pane (citation chip flow). The launcher flow passes nothing and
      // lands on a bare spotlight.
      open: (entryId = null) =>
        set({ isOpen: true, selectedEntryId: entryId }, false, 'knowledgeBrowser/open'),
      // Closing wipes ALL UI state so reopening from the launcher never
      // starts on a stale query, filter, or detail pane. The chip flow
      // re-seeds via `open(entryId)` so its deep-link path is unaffected.
      close: () => set({ ...initialState }, false, 'knowledgeBrowser/close'),
      selectEntry: (entryId) =>
        set({ selectedEntryId: entryId }, false, 'knowledgeBrowser/selectEntry'),
      // Selection is bound to the current result set: when the query or
      // filter changes (or the user clears the box), the cached selection
      // is no longer guaranteed to be in view, so drop it. The dialog's
      // auto-select effect picks the new first row when results land.
      setQuery: (query) =>
        set({ query, selectedEntryId: null }, false, 'knowledgeBrowser/setQuery'),
      toggleKind: (kind) =>
        set(
          (prev) => ({
            kinds: prev.kinds.includes(kind)
              ? prev.kinds.filter((k) => k !== kind)
              : [...prev.kinds, kind],
            selectedEntryId: null,
          }),
          false,
          'knowledgeBrowser/toggleKind'
        ),
      setKinds: (kinds) =>
        set({ kinds, selectedEntryId: null }, false, 'knowledgeBrowser/setKinds'),
      reset: () => set(initialState, false, 'knowledgeBrowser/reset'),
    }),
    { name: 'knowledgeBrowser' }
  )
);

export const selectIsOpen = (state: KnowledgeBrowserState) => state.isOpen;
export const selectSelectedEntryId = (state: KnowledgeBrowserState) => state.selectedEntryId;
export const selectQuery = (state: KnowledgeBrowserState) => state.query;
export const selectKinds = (state: KnowledgeBrowserState) => state.kinds;
